import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from "@shared/store/dialog.duck";
import Dialog from '@shared/ui/molecules/Dialog';
import DialogHeader from '@shared/dialogs/DialogHeader';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { OfficeAndDateContentCard } from '@shared/ui/molecules/OfficeAndDateContentCard';
import styled from "styled-components";
import TextField from '@shared/ui/atoms/TextField';
import { UserSearchDropDownList } from '@shared/ui/molecules/UserSearchDropDownList';
import { BasicUserInfo } from '@shared/models/user.models';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { Switch } from '@shared/ui/atoms/Switch';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { cancelVisitorRequest, createVisitorBooking, VisitorBooking } from "./data/visitor-booking-service";
import { selectCalendarDate } from "@shared/store/dashboard.duck";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import { selectSelectedOffice } from "../../../../store/officeView.duck";
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { getRootOffice } from "@shared/utils/OfficeHelper";
import { loadVisitorBookings } from "./data/visitorBooking.duck";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { TimeRangeSelector } from "@features/meeting-rooms/pages/components/TimeRangeSelector";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { BorderlessButton } from '@shared/ui/atoms/buttons/BorderlessButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function AddVisitorDialog(props: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [visitingUser, setVisitingUser] = useState<BasicUserInfo | undefined>();
  const [visitorName, setVisitorName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [dueInHours, setDueInHours] = useState(9);
  const [dueInMinutes, setDueInMinutes] = useState(0);
  const [enableRepeatBooking, setEnableRepeatBooking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [daysToRepeat, setDaysToRepeat] = useState<Moment[]>([]);
  const [bookingId, setBookingId] = useState<number | undefined>();
  const calendarDate = useSelector(selectCalendarDate);

  const selectedOffice = useSelector(selectSelectedOffice);
  const allOffices = useSelector(selectAllOfficeEntities);

  useEffect(() => {
    const booking = props?.payload?.visitorBooking;
    if (booking) {
      // setVisitingUser(booking.visitingUserId)
      setVisitorName(booking.visitorName);
      setCompanyName(booking.companyName);
      setDueInHours(booking.timeDueHours);
      setDueInMinutes(booking.timeDueMinutes);
      setBookingId(booking.id)
      setIsEditing(true);
    }
  }, [props.payload.visitorBooking]);

  const repeatableDays = useMemo(() => {
    const date = props.payload?.date || calendarDate || moment();
    let retVal: Moment[] = [];
    for (let i = 1; i < 8; i++) {
      const repeatableDay = date.clone().add(i, 'days');
      if (repeatableDay.day() !== 6 && repeatableDay.day() !== 0) {
        retVal.push(repeatableDay);
      }
    }
    return retVal;
  }, [props.payload.date, calendarDate])

  const rootOffice = useMemo(() => {
    if (selectedOffice?.parentId) {
      return getRootOffice(allOffices, selectedOffice);
    } else if (selectedOffice) {
      return selectedOffice;
    }
  }, [allOffices, selectedOffice]);

  const date = useSelector(selectCalendarDate);

  const updateDayToRepeat = (date: Moment) => {
    if (daysToRepeat.includes(date)) {
      setDaysToRepeat(daysToRepeat.filter(d => d !== date));
    } else {
      setDaysToRepeat([...daysToRepeat, date]);
    }
  }

  const close = () => {
    dispatch(closeDialog());
  }

  const userSelected = (user: BasicUserInfo) => {
    setVisitingUser(user);
  }

  const completeBooking = async () => {
    setLoading(true);
    try {
      if (rootOffice) {
        await createVisitorBooking({
          visitorBookingId: props.payload.visitorBooking?.id,
          date: date.format(DATE_FORMAT),
          visitingUserId: visitingUser?.userId,
          visitorName: visitorName,
          companyName: companyName,
          dueInHours: dueInHours,
          dueInMinutes: dueInMinutes,
          visitingOfficeId: rootOffice.id,
        });
        if (daysToRepeat) {
          await Promise.all(daysToRepeat.map(async (day) => {
            return await createVisitorBooking({
              visitorBookingId: props.payload.visitorBooking?.id,
              date: day.format(DATE_FORMAT),
              visitingUserId: visitingUser?.userId,
              visitorName: visitorName,
              companyName: companyName,
              dueInHours: dueInHours,
              dueInMinutes: dueInMinutes,
              visitingOfficeId: rootOffice.id,
            });
          }))
        }
        dispatch(loadVisitorBookings({date: date.format(DATE_FORMAT)}));
      }
    } finally {
      setLoading(false);
    }
    close();
  }

  const cancelVisitor = async () => {
    if (isEditing && bookingId) {
      try {
        setLoading(true);
        await cancelVisitorRequest(bookingId);
        dispatch(loadVisitorBookings({date: date.format(DATE_FORMAT)}));
      } finally {
        setLoading(false);
      }
    }
  }

  const onTimeChange = (from: Moment, to?: Moment) => {
    setDueInHours(from.hours());
    setDueInMinutes(from.minutes());
  }

  return (
    <>
      <DialogWrapper>
        <Dialog isOpen={true} onClose={close}>
          <DialogHeader title={isEditing ? 'visitor-booking.edit-visitor' : 'visitor-booking.add-visitor' } />
          <OfficeAndDateContentCard style={{marginBottom: 24}}
                                    office={rootOffice}
                                    date={props.payload.date} />

          {loading ? <>
            <Center>
              <LoadingSpinner hideBorder={true} hideText={true} />
            </Center>
          </> : <>
            <TextField onChange={setVisitorName} value={visitorName} fullWidth={true} label={'visitor-booking.visitor-name'} />
            <TextField onChange={setCompanyName} value={companyName} fullWidth={true} label={'visitor-booking.company-name'} />
            <div>
              <BodyRegular colour={Colours.darkGrey} weight={600} style={{marginBottom: 8}}>{t('visitor-booking.due-in')}</BodyRegular>
              <TimeRangeSelector style={{marginBottom: 16}} hour={dueInHours}
                                 minutes={dueInMinutes}
                                 onTimeChange={onTimeChange}
                                 hideTimeTo={true} hideLabels={true} />
            </div>
            <div style={{marginBottom: 24}}>
              <BodyRegular colour={Colours.darkGrey} weight={600} style={{marginBottom: 8}}>{t('visitor-booking.meeting-with')}</BodyRegular>
              <UserSearchDropDownList closeOnSelect={true} onSelect={userSelected} presetSearch={props.payload.visitorBooking?.visitingUserName} />
            </div>

            {visitingUser && <UserAvatarAndName firstName={visitingUser.firstName} lastName={visitingUser.lastName} colour={"blue"} />}

            <Switch label={'visitor-booking.repeat-booking'}
                    value={enableRepeatBooking}
                    onChange={setEnableRepeatBooking}
                    style={{marginBottom: 24}} />

            {enableRepeatBooking && <Row>
              {repeatableDays.map((rd: Moment, key: number) => (<ToggleableOutlineButton key={key}
                                                                                         size={ButtonSize.Small}
                                                                                         isActive={daysToRepeat.includes(rd)}
                                                                                         click={() => updateDayToRepeat(rd)}
                                                                                         text={rd.format('ddd Do')} />))}
            </Row>}

            <PrimaryButton text={isEditing ? 'visitor-booking.edit-visitor' : 'visitor-booking.add-visitor'} fullWidth={true} click={completeBooking} />

            {isEditing && <BorderlessButton text={'visitor-booking.cancel'}
                                            style={{marginTop: 12}}
                                            fullWidth={true}
                                            click={cancelVisitor} />}
          </>}

        </Dialog>
      </DialogWrapper>
    </>
  )
}

interface Props {
  payload: {
    visitorBooking?: VisitorBooking,
    office: OfficeEntity;
    date: Moment;
  };
}

const DialogWrapper = styled.div`
  .dialog {
    min-width: 524px;
    padding: 64px 64px 52px 64px;
  }
  .userAvatarAndName {
    margin-bottom: 24px;
  }
  .switch p {
    font-weight: 600;
    color: ${Colours.darkGrey};
  }
  .fromTimeRangeSelector__inputContainer {
    margin: 0 !important;
    max-width: 200px;
  }
`

const Row = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  margin-bottom: 32px;
  button {
    padding: 4px 8px;
    min-width: unset;
    flex: 1;
    height: unset;
  }
  .toggleableOutlineButton {
    margin-right: 8px;
  }
  .toggleableOutlineButton:last-child {
    margin-right: 0;
  }
`
